/**
 * A job's punch list (Duncan 2026-10-26): "allow the E3 Project Manager to create a punchlist item and assign it to a
 * subcontractor ... the subcontractor then reply with pictures and such showing that it's done ... alerts the E3
 * Project Manager that it's done". Add punch item puts one on a sub's task -- on a lighting task its line too -- with
 * where, what and photos. Spot-check findings are punch items here as well. E3 Subs keeps them (routes/taskWork.js has
 * the routes), emails the sub's people at 7 AM with what's new, and the sub marks one done there (a note is enough); the
 * bell tells the PMs (subPunch.js). A PM deletes an item while it's still open -- a spot check's finding too (Duncan
 * 2026-10-26: "If I wanted to undo this damage report/punchlist item, how would I do that"), its line then back in
 * Lines to walk.
 * Duncan 2026-10-26, per task ("I don't wanna click walk it for every single line item, it needs to be per task"):
 * Walk N lines opens a task's picked lines in one window, each with Passed or Findings on its row (one line at a time:
 * no "pass the rest", his pick).
 * Duncan 2026-10-26, for a big job ("there might be lots of punch items on a larger job ... It needs to be very good";
 * the mockup he OK'd): the four counts switch the view -- To verify (amber: what the sub says is done, the oldest first,
 * with Review one by one), Open with the subs (grouped by sub), Lines to walk (a task at a time) and Verified -- each
 * with search and the sub and task filters, one-line rows, and Show more instead of one endless page. The tab opens on
 * To verify whenever something waits there. Review one by one shows an item's photos big, and Verify or Send back moves
 * straight to the next (V, S and the arrow keys too).
 * Duncan 2026-10-26 ("I dont like the giant expansive list when you first open the tab ... what about a smaller list that
 * can be scrolled on? could have that pencil scroll indicator"): each view's list sits in a short box that scrolls inside
 * the tab, with the pencil-thin rail (ScrollRail.js) beside it marking its sections -- To verify by how long it has
 * waited, Open and Lines to walk by sub, Verified by day -- instead of Show more.
 * Duncan 2026-10-26 ("I don't think the user should have to click on any of the four tabs to see stuff ... all on the
 * same page, just separated into sections"; the mockup he OK'd): the four sections are all on the page at once, each in
 * its own short box; the counts at the top jump to their section. And ("a second pencil scroller over to the left of it
 * for all of the sections ... in a small laptop screen you can see where you're scrolling into"): a page-long rail on
 * the left, kept in view, with a dot for each section and the part of the page on screen; on a screen 900px wide or
 * more, when the page scrolls.
 * Duncan 2026-10-26 ("the same pop-up feature that the subcontractor site has on the punchlist items. It's the same
 * thing we do with our AR when you click on it floats up"; the mockup he OK'd): a click on an item lifts it out of the
 * list (Lift.js) -- its photos big, its history, and Verify / Send back / Delete -- instead of opening it in place;
 * after Verify or Send back it settles back into the list.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  getProjectPunch, getTaskWork, getTaskWorkProgress, addPunchItem, addPunchPhoto, verifyPunchItem, sendBackPunchItem, removePunchItem,
  recordSpotCheck, addWorkPhoto,
} from '../../api';
import { Icon } from '../ui';
import { ErrorBanner, PrimaryButton, SecondaryButton, ModalShell, Field, SubStyles } from './SubUi';
import { newRequestId, findingTypeOf, FINDING_TYPES, whoOf, Thumb, AddPhoto, PhotoViewer, redPill, amberPill, bluePill, grayPill, greenPill } from './LineWork';
import ScrollRail from './ScrollRail';
import Lift from './Lift';
import ConfirmDialog from '../ConfirmDialog';
import useConfirm from '../../hooks/useConfirm';
import { photoBody } from '../../lib/photos';

const muted = { fontSize: 12.5, color: 'var(--text-3)' };
const linkBtn = { border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--accent-text)', fontFamily: 'inherit', whiteSpace: 'nowrap' };
const smallBtn = { height: 30, padding: '0 11px', fontSize: 13 };
const select = { height: 34, padding: '0 10px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-1)', fontSize: 13.5, fontFamily: 'inherit', maxWidth: '100%', boxSizing: 'border-box' };
const sourcePill = { ...grayPill, border: '1px solid var(--border-subtle)', fontWeight: 500 };
// What waits on E3 is amber (Duncan: "visual emphasis on the items to verify that the subcontractor says are done")
const amberBtn = { background: 'var(--warning)', border: '1px solid color-mix(in oklch, var(--warning) 70%, black)', boxShadow: 'none' };
// A list's box: about ten rows, shorter on a small screen; it scrolls inside, the rail beside it (Duncan's pick).
const BOX_MAX = 'max(200px, min(380px, calc(100vh - 360px)))';

const CSS = `
  .pl-row { display: flex; align-items: center; gap: 8px; padding: 7px 12px; min-height: 42px; box-sizing: border-box; }
  .pl-main { display: flex; align-items: center; gap: 6px; flex: 1 1 auto; min-width: 0; text-align: left; border: none; background: transparent;
    padding: 0; cursor: pointer; font: inherit; color: var(--text-1); }
  .pl-main:focus-visible, .pl-tile:focus-visible, .pl-group:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--accent-ring); border-radius: 6px; }
  .pl-what { font-size: 13.5px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 0 1 auto; max-width: 45%; }
  .pl-meta { font-size: 12.5px; color: var(--text-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1 1 0; min-width: 0; }
  .pl-age { font-size: 12px; color: var(--text-3); white-space: nowrap; flex-shrink: 0; }
  .pl-acts { display: flex; gap: 6px; flex-shrink: 0; }
  .pl-tile { text-align: left; font: inherit; cursor: pointer; color: var(--text-1); }
  /* A sub's heading is a dark band, set apart from its items (Duncan 2026-10-26, on E3 Subs' punch list: "I can't tell
     the difference between the header and the task itself"; E3 Subs' values, so the two apps match). */
  .pl-group { --pl-head-bg: #3a3833; --pl-head-hover: #46443e; --pl-head-fg: #ffffff; --pl-head-muted: #cfccc6;
    display: flex; align-items: center; gap: 8px; width: 100%; padding: 10px 12px; border: none; background: var(--pl-head-bg);
    font: inherit; font-size: 15px; font-weight: 700; color: var(--pl-head-fg); cursor: pointer; text-align: left; }
  .app.dark .pl-group, .theme-dark .pl-group { --pl-head-bg: #121110; --pl-head-hover: #1b1a18; --pl-head-fg: #f3f1ec; --pl-head-muted: #a8a39b; }
  .pl-group:hover { background: var(--pl-head-hover); }
  .pl-gname { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .pl-sec { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin: 20px 0 8px; font-size: 14.5px; font-weight: 650; color: var(--text-1); scroll-margin-top: 12px; }
  .pl-review { display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr); gap: 16px; }
  .pl-big { height: 340px; }
  /* An item lifted out of the list (Lift.js, AR's notes lift-in-blur): its row, opened up. */
  .lift .pl-row { padding-right: 48px; }
  .lift .pl-main { cursor: default; }
  .lift.is-open .pl-main { flex-wrap: wrap; row-gap: 2px; }
  .lift.is-open .pl-what { white-space: normal; max-width: 100%; }
  .lift.is-open .pl-meta { white-space: normal; flex: 1 1 240px; }
  .pl-lift-big { height: 320px; }
  @media (max-width: 720px) {
    .pl-row { flex-wrap: wrap; }
    .pl-main { flex-wrap: wrap; flex-basis: 100%; }
    .pl-what { white-space: normal; max-width: none; }
    .pl-gname { white-space: normal; overflow-wrap: anywhere; }
    .pl-meta { white-space: normal; flex-basis: 100%; }
    .pl-review { grid-template-columns: minmax(0, 1fr); }
    .pl-big { height: 200px; }
    .pl-lift-big { height: 220px; }
  }
`;

const DAY = 86400000;
/** "today 9:40 AM", "yesterday 4:15 PM", "Oct 24" (a year when it isn't this one). */
function whenOf(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const days = Math.round((new Date().setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / DAY);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (days === 0) return `today ${time}`;
  if (days === 1) return `yesterday ${time}`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}) });
}
/** Whole days since a moment, by the calendar here (0 = today). */
const daysSince = (iso) => (iso ? Math.max(0, Math.round((new Date().setHours(0, 0, 0, 0) - new Date(iso).setHours(0, 0, 0, 0)) / DAY)) : null);
const lastOf = (item, kind) => [...(item.thread || [])].reverse().find((e) => e.kind === kind) || null;
const photosOf = (item) => (item.thread || []).flatMap((e) => e.photos || []);
const taskLabel = (t) => `${t.code ? `${t.code} · ` : ''}${t.name}${t.area ? ` · ${t.area}` : ''}`;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const doneAt = (x) => (lastOf(x, 'DONE') || {}).at || '';
const verifiedAt = (x) => (lastOf(x, 'VERIFIED') || {}).at || '';
const pad2 = (n) => String(n).padStart(2, '0');
const dayKeyOf = (iso) => { const d = new Date(iso || 0); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };
/** A day for the rail: Today, Yesterday, or Oct 24. */
const dayLabelOf = (iso) => {
  const d = daysSince(iso);
  if (d === 0) return 'Today';
  if (d === 1) return 'Yesterday';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
/** Where a section starts, for the rail: on its first row. */
const railMark = (id, label, count) => ({ 'data-rail-id': id, 'data-rail-label': label, 'data-rail-count': count });
// A thread's steps, in E3's words
const STEP = { ADDED: 'Added', DONE: 'Marked done', SENT_BACK: 'Sent back', VERIFIED: 'Verified' };

/** How long a row has stood: what waits on E3 since the sub marked it done, what's open since it was added. */
function ageOf(item) {
  if (item.status === 'DONE') { const d = daysSince(doneAt(item)); return d === 0 ? 'done today' : `waiting ${plural(d, 'day')}`; }
  if (item.status === 'VERIFIED') return `verified ${whenOf(verifiedAt(item))}`;
  const d = daysSince(item.createdAt);
  return d === 0 ? 'added today' : `open ${plural(d, 'day')}`;
}

export default function PunchListTab({ projectId }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(null);
  const [search, setSearch] = useState('');
  const [subFilter, setSubFilter] = useState('');
  const [taskFilter, setTaskFilter] = useState('');
  const [sentBackOnly, setSentBackOnly] = useState(false);
  const [newestFirst, setNewestFirst] = useState(false);
  const [folded, setFolded] = useState(() => new Set()); // Open: the subs folded away
  const [lift, setLift] = useState(null); // { id, rect, from, opts }: the item lifted out of the list (Lift)
  const liftClose = useRef(null); // the lift's own close, to settle it back after Verify, Send back or Delete
  const [back, setBack] = useState(null); // { id, note }: Send back being written
  const [adding, setAdding] = useState(null); // Add punch item: null closed, '' open, a task id to start on it
  const [walking, setWalking] = useState(null); // the task being walked
  const [reviewing, setReviewing] = useState(null); // the To verify ids being reviewed one by one, in order
  const [viewer, setViewer] = useState(null); // { list, index }
  const { confirm, confirmDialogProps } = useConfirm();

  const load = useCallback(() => getProjectPunch(projectId)
    .then((d) => { setData(d); setLoadError(null); })
    .catch((e) => setLoadError(e.message || 'The punch list didn’t load')), [projectId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!note) return undefined;
    const t = setTimeout(() => setNote(null), 6000);
    return () => clearTimeout(t);
  }, [note]);
  // Photo links last about 15 minutes: a broken one reads the list again, once a minute at most.
  const lastReload = useRef(0);
  const onExpired = useCallback(() => {
    if (Date.now() - lastReload.current < 60000) return;
    lastReload.current = Date.now();
    load();
  }, [load]);

  useEffect(() => {
    if (lift && data && !(data.items || []).some((x) => x.id === lift.id)) setLift(null);
  }, [lift, data]);
  const liftedItem = lift && data ? (data.items || []).find((x) => x.id === lift.id) || null : null;

  const tasks = useMemo(() => (data && data.tasks) || [], [data]);
  const taskById = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  const lineById = useMemo(() => new Map(((data && data.lines) || []).map((l) => [l.id, l])), [data]);
  const subs = useMemo(() => {
    const m = new Map();
    tasks.forEach((t) => { if (t.sub && t.sub.id != null && !m.has(t.sub.id)) m.set(t.sub.id, t.sub); });
    return [...m.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [tasks]);
  const subOf = (item) => (item.company && item.company.name) || ((taskById.get(Number(item.taskId)) || {}).sub || {}).name || 'The sub';

  // The line an item or a walk names: Summit's own (its Line #, building, room or area, proposed fixture), else E3 Subs'.
  const lineOf = (lineId, fallback) => {
    const l = lineById.get(Number(lineId));
    if (l) return { no: l.line_no, place: [l.building, l.area].filter(Boolean).join(' · '), fixture: l.pro_type };
    if (fallback) return { no: fallback.lineNo, place: [fallback.building, fallback.area].filter(Boolean).join(' · '), fixture: null };
    return null;
  };
  const shown = (taskId) => {
    const t = taskById.get(Number(taskId));
    if (taskFilter) return String(taskId) === taskFilter;
    if (subFilter) return !!t && String(t.sub.id) === subFilter;
    return true;
  };
  const q = search.trim().toLowerCase();
  const found = (...words) => !q || words.some((w) => w != null && String(w).toLowerCase().includes(q));
  const itemFound = (item) => {
    const task = taskById.get(Number(item.taskId));
    const line = item.lineId ? lineOf(item.lineId, item.line) : null;
    return found(item.what, item.where, line && line.no != null ? `line ${line.no}` : null, line && line.place, task && task.code, task && task.name, task && task.area, subOf(item));
  };
  const items = ((data && data.items) || []).filter((x) => shown(x.taskId) && itemFound(x));
  const toVerify = items.filter((x) => x.status === 'DONE').sort((a, b) => String(doneAt(a)).localeCompare(String(doneAt(b))));
  const openAll = items.filter((x) => x.status === 'OPEN');
  const openItems = (sentBackOnly ? openAll.filter((x) => x.sentBack) : openAll).sort((a, b) => (b.sentBack ? 1 : 0) - (a.sentBack ? 1 : 0)
    || (newestFirst ? String(b.createdAt || '').localeCompare(String(a.createdAt || '')) : String(a.createdAt || '').localeCompare(String(b.createdAt || ''))));
  const verified = items.filter((x) => x.status === 'VERIFIED').sort((a, b) => String(verifiedAt(b)).localeCompare(String(verifiedAt(a))));
  const toWalk = ((data && data.toWalk) || []).filter((w) => {
    if (!shown(w.taskId)) return false;
    const task = taskById.get(Number(w.taskId));
    const line = lineOf(w.lineId);
    return found(task && task.code, task && task.name, task && task.area, task && task.sub && task.sub.name, line && line.no != null ? `line ${line.no}` : null, line && line.place, line && line.fixture);
  });
  const walkCount = new Map();
  toWalk.forEach((w) => walkCount.set(Number(w.taskId), (walkCount.get(Number(w.taskId)) || 0) + 1));
  const walkTasks = [...tasks.map((t) => t.id).filter((id) => walkCount.has(id)), ...[...walkCount.keys()].filter((id) => !taskById.has(id))];

  // A row's place: where, the task's code, the line, the sub (not in a sub's own group), photos, a send-back note.
  const metaOf = (item, { withSub = true } = {}) => {
    const task = taskById.get(Number(item.taskId));
    const line = item.lineId ? lineOf(item.lineId, item.line) : null;
    const where = item.where || (line && line.place) || null;
    const n = photosOf(item).length;
    const said = item.status === 'OPEN' && item.sentBack ? (lastOf(item, 'SENT_BACK') || {}).note : null;
    return [where, task ? (task.code || task.name) : null, line && line.no != null ? `Line ${line.no}` : null, withSub ? subOf(item) : null,
      n ? plural(n, 'photo') : null, said ? `“${said.length > 60 ? `${said.slice(0, 60)}…` : said}”` : null].filter(Boolean).join(' · ');
  };

  const run = async (key, fn, said, onDone) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      if (onDone) onDone();
      if (said) setNote(said);
      await load();
      return true;
    } catch (e) {
      setError(e.message || 'That didn’t go through');
      return false;
    } finally {
      setBusy(null);
    }
  };
  const settleLift = () => { if (liftClose.current) liftClose.current(); };
  const verify = (item, onDone) => run(`verify-${item.id}`, () => verifyPunchItem(item.taskId, item.id, { requestId: newRequestId() }), 'Verified.', onDone);
  const sendBack = async (item, onDone) => {
    const ok = await run(`back-${item.id}`, () => sendBackPunchItem(item.taskId, item.id, { note: back.note.trim(), requestId: newRequestId() }),
      `Sent back. ${subOf(item)} hears about it in the next 7 AM email.`, onDone);
    if (ok) setBack(null);
  };
  const remove = async (item, onDone) => {
    // A spot check's only finding on its line takes the line back to Lines to walk, to walk again.
    const backToWalk = item.source === 'SPOT_CHECK' && !((data && data.items) || [])
      .some((x) => x.id !== item.id && x.source === 'SPOT_CHECK' && Number(x.lineId) === Number(item.lineId));
    const line = backToWalk ? lineOf(item.lineId, item.line) : null;
    const lineName = line && line.no != null ? `Line ${line.no}` : 'Its line';
    const yes = await confirm({
      title: 'Delete this punch item?',
      message: `“${item.what}” comes off ${subOf(item)}’s punch list${backToWalk ? `, and ${lineName === 'Its line' ? 'its line' : lineName} goes back to Lines to walk, to walk again` : ''}. E3 Subs keeps it for the record.`,
      confirmLabel: 'Delete', danger: true,
    });
    if (yes) {
      await run(`remove-${item.id}`, () => removePunchItem(item.taskId, item.id, { requestId: newRequestId() }),
        backToWalk ? `Deleted. ${lineName} is back in Lines to walk.` : 'Deleted.', onDone);
    }
  };
  // Lift an item out of the list from its row (`from`: the button clicked, where the focus goes back to);
  // `writeBack`: straight to the send-back note (the row's own Send back).
  const liftItem = (item, from, opts, writeBack = false) => {
    const el = from.closest('[data-pl-row]');
    if (!el) return;
    const r = el.getBoundingClientRect();
    setError(null);
    setBack(writeBack ? { id: item.id, note: '' } : null);
    setLift({ id: item.id, rect: { left: r.left, top: r.top, width: r.width, height: r.height }, from, opts });
  };

  // The page rail: where each section starts down the page, measured as the page lays out and whenever it changes size.
  const pageRef = useRef(null);
  const [pageRail, setPageRail] = useState({ on: false, items: [], head: 0 });
  const pageRailKey = useRef(JSON.stringify(pageRail)); // set again only when the measure changes (RailBox's railKey)
  const sectionCounts = { verify: toVerify.length, open: openAll.length, walk: toWalk.length, verified: verified.length };
  const countsRef = useRef(sectionCounts);
  countsRef.current = sectionCounts;
  useLayoutEffect(() => {
    const measure = () => {
      // The job's header stays at the top of the window (ProjectDetail): the rail starts under it, a jump lands under it.
      const headEl = document.querySelector('[data-project-head]');
      const head = headEl ? Math.round(headEl.getBoundingClientRect().height) : 0;
      const items = [['verify', 'To verify'], ['open', 'Open'], ['walk', 'Lines to walk'], ['verified', 'Verified']].map(([key, label]) => {
        const el = document.getElementById(`punch-${key}`);
        return el ? { id: key, label, count: countsRef.current[key], top: Math.round(el.getBoundingClientRect().top + window.scrollY - head - 12) } : null;
      }).filter(Boolean);
      const on = window.innerWidth >= 900 && document.documentElement.scrollHeight > window.innerHeight + 40 && items.length > 0;
      const next = { on, items, head };
      const key = JSON.stringify(next);
      if (key === pageRailKey.current) return;
      pageRailKey.current = key;
      setPageRail(next);
    };
    measure();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (ro && pageRef.current) ro.observe(pageRef.current);
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  });

  if (loadError) {
    return (
      <div style={{ display: 'grid', gap: 10 }}>
        <ErrorBanner>{loadError}</ErrorBanner>
        <div><SecondaryButton onClick={load}>Try again</SecondaryButton></div>
      </div>
    );
  }
  if (!data) return <div style={{ ...muted, padding: 20 }}>Loading the punch list…</div>;

  const pills = (item) => {
    const out = [];
    if (item.status === 'DONE') out.push(<span key="s" style={amberPill}>Done</span>);
    else if (item.status === 'VERIFIED') out.push(<span key="s" style={grayPill}>Verified</span>);
    else out.push(<span key="s" style={redPill}>{item.sentBack ? 'Sent back' : 'Open'}</span>);
    if (item.source === 'SPOT_CHECK') out.push(<span key="src" style={sourcePill}>Spot check{item.findingType ? ` · ${findingTypeOf(item.findingType)}` : ''}</span>);
    return out;
  };

  // An item's history and photos, opened under its row: Send back's note, and Delete for an open item E3 added.
  // The lifted item: its row as the list shows it (its age where Send back and Verify were), then its photos big -- the
  // sub's first -- its history, and what you can do with it.
  const liftLine = (item, opts) => (
    <div className="pl-row" style={{ borderLeft: `4px solid ${item.status === 'DONE' ? 'var(--warning)' : 'transparent'}` }}>
      <div className="pl-main">
        {pills(item)}
        <span className="pl-what">{item.what}</span>
        <span className="pl-meta">{metaOf(item, opts)}</span>
      </div>
      <span className="pl-age" style={item.status === 'DONE' && daysSince(doneAt(item)) > 0 ? { color: 'var(--warning)', fontWeight: 600 } : undefined}>{ageOf(item)}</span>
    </div>
  );
  const liftPanel = (item) => {
    const writing = back && back.id === item.id;
    const theirs = item.status === 'DONE' ? ((lastOf(item, 'DONE') || {}).photos || []) : [];
    const photos = [...theirs, ...photosOf(item).filter((p) => !theirs.some((t) => t.id === p.id))];
    return (
      <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '12px 16px 14px', display: 'grid', gap: 12 }}>
        {photos.length > 0 && <LiftPhotos photos={photos} theirs={theirs.length} onOpen={(k) => setViewer({ list: photos, index: k })} onExpired={onExpired} />}
        <div style={{ display: 'grid', gap: 8 }}>
          {(item.thread || []).map((e, i) => (
            <div key={i} style={{ fontSize: 13, color: 'var(--text-2)' }}>
              <strong style={{ color: 'var(--text-1)', fontWeight: 600 }}>{STEP[e.kind] || e.kind}</strong>
              {e.by ? ` by ${whoOf(e.by)}` : ''}{e.at ? `, ${whenOf(e.at)}` : ''}
              {e.note && <div style={{ fontSize: 13.5, color: 'var(--text-1)', marginTop: 2, whiteSpace: 'pre-wrap' }}>{e.note}</div>}
            </div>
          ))}
        </div>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        {item.status === 'DONE' && (writing ? (
          <div style={{ display: 'grid', gap: 8 }}>
            <textarea autoFocus value={back.note} rows={2} maxLength={2000} placeholder="What still needs doing" aria-label="What still needs doing"
              onChange={(ev) => setBack({ ...back, note: ev.target.value })}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-1)', fontSize: 13.5, fontFamily: 'inherit', lineHeight: 1.4, resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <PrimaryButton busy={busy === `back-${item.id}`} disabled={!back.note.trim()} onClick={() => sendBack(item, settleLift)} style={smallBtn}>Send back</PrimaryButton>
              <button type="button" style={{ ...linkBtn, color: 'var(--text-3)' }} onClick={() => setBack(null)}>Cancel</button>
              <span style={muted}>It goes back to {subOf(item)} as open, in their next 7 AM email.</span>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <SecondaryButton onClick={() => setBack({ id: item.id, note: '' })} disabled={!!busy}>Send back</SecondaryButton>
            <PrimaryButton busy={busy === `verify-${item.id}`} onClick={() => verify(item, settleLift)}><Icon name="check" size={14} />Verify</PrimaryButton>
          </div>
        ))}
        {item.status === 'OPEN' && (
          <div>
            <button type="button" style={{ ...linkBtn, color: 'var(--danger)' }} disabled={busy === `remove-${item.id}`} onClick={() => remove(item, settleLift)}>Delete</button>
          </div>
        )}
      </div>
    );
  };

  // One line per item: what, where, how long it has stood; the sub's Done ones carry Send back and Verify, and an amber edge.
  const row = (item, i, opts = {}, mark = null) => {
    const lifted = !!lift && lift.id === item.id;
    const done = item.status === 'DONE';
    return (
      <div key={item.id} data-pl-row {...(mark || {})} style={{ borderTop: i ? '1px solid var(--border-subtle)' : 'none', borderLeft: `4px solid ${done ? 'var(--warning)' : 'transparent'}`, background: 'var(--bg-surface)', visibility: lifted ? 'hidden' : undefined }}>
        <div className="pl-row">
          <button type="button" className="pl-main" onClick={(e) => liftItem(item, e.currentTarget, opts)} aria-haspopup="dialog" title={item.what}>
            {pills(item)}
            <span className="pl-what">{item.what}</span>
            <span className="pl-meta">{metaOf(item, opts)}</span>
          </button>
          <span className="pl-age" style={done && daysSince(doneAt(item)) > 0 ? { color: 'var(--warning)', fontWeight: 600 } : undefined}>{ageOf(item)}</span>
          {done && (
            <span className="pl-acts">
              <SecondaryButton onClick={(e) => liftItem(item, e.currentTarget, opts, true)} style={smallBtn}>Send back</SecondaryButton>
              <PrimaryButton busy={busy === `verify-${item.id}`} onClick={() => verify(item)} style={smallBtn}><Icon name="check" size={13} />Verify</PrimaryButton>
            </span>
          )}
        </div>
      </div>
    );
  };
  const filtered = !!(q || subFilter || taskFilter);
  const nothing = (text) => <div style={{ ...muted, padding: '6px 2px' }}>{filtered ? 'Nothing matches. Clear the search or filters to see everything.' : text}</div>;
  const resetKey = [q, subFilter, taskFilter].join('|');
  // Each section's first row and its count: for the rail
  const firstsOf = (rows, keyOf) => {
    const count = new Map();
    const first = new Set();
    rows.forEach((x) => { const k = keyOf(x); if (!count.has(k)) first.add(x); count.set(k, (count.get(k) || 0) + 1); });
    return { count, first };
  };

  // The views
  const waitKey = (x) => { const d = daysSince(doneAt(x)); return d === 0 ? 'Today' : plural(d, 'day'); };
  const verifySections = firstsOf(toVerify, waitKey);
  const verifyView = toVerify.length ? (
    <RailBox title="Waiting" resetKey={resetKey}>
      {toVerify.map((x, i) => row(x, i, {}, verifySections.first.has(x) ? railMark(`w-${waitKey(x)}`, waitKey(x), verifySections.count.get(waitKey(x))) : null))}
    </RailBox>
  ) : nothing('Nothing waiting on you.');

  const groups = new Map();
  openItems.forEach((x) => {
    const key = subOf(x);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(x);
  });
  const groupOrder = [...groups.keys()].sort((a, b) => groups.get(b).length - groups.get(a).length || a.localeCompare(b));
  const openView = openItems.length ? (
    <RailBox title="Subs" resetKey={`${resetKey}|${sentBackOnly}|${newestFirst}`}>
      {groupOrder.map((key) => {
        const rows = groups.get(key);
        const isFolded = folded.has(key);
        const sent = rows.filter((x) => x.sentBack).length;
        return (
          <div key={key} {...railMark(`g-${key}`, key, rows.length)}>
            <button type="button" className="pl-group" aria-expanded={!isFolded}
              onClick={() => setFolded((st) => { const n = new Set(st); if (n.has(key)) n.delete(key); else n.add(key); return n; })}>
              <span style={{ display: 'inline-flex', transform: isFolded ? 'rotate(-90deg)' : 'none', transition: 'transform .15s', color: 'var(--pl-head-muted)' }}><Icon name="caret" size={14} /></span>
              <span className="pl-gname">{key}</span>
              <span style={redPill}>{rows.length} open</span>
              {sent > 0 && <span style={{ ...muted, color: 'var(--pl-head-muted)', fontWeight: 500 }}>{sent} sent back</span>}
            </button>
            {!isFolded && rows.map((x, i) => row(x, i + 1, { withSub: false }))}
          </div>
        );
      })}
    </RailBox>
  ) : sentBackOnly && openAll.length ? <div style={{ ...muted, padding: '6px 2px' }}>Nothing sent back.</div> : nothing('Nothing open with the subs.');

  const walkSubOf = (id) => { const t = taskById.get(id); return (t && t.sub && t.sub.name) || 'The sub'; };
  const walkSections = firstsOf(walkTasks, walkSubOf);
  const walkLines = new Map();
  walkTasks.forEach((id) => walkLines.set(walkSubOf(id), (walkLines.get(walkSubOf(id)) || 0) + walkCount.get(id)));
  const walkView = walkTasks.length ? (
    <>
      <RailBox title="Subs" resetKey={resetKey}>
        {walkTasks.map((id, i) => {
          const task = taskById.get(id) || null;
          const n = walkCount.get(id);
          const mark = walkSections.first.has(id) ? railMark(`s-${walkSubOf(id)}`, walkSubOf(id), walkLines.get(walkSubOf(id))) : {};
          return (
            <div key={id} {...mark} className="pl-row" style={{ borderTop: i ? '1px solid var(--border-subtle)' : 'none', background: 'var(--bg-surface)', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>{task ? `${task.code ? `${task.code} · ` : ''}${task.name}` : `Task ${id}`}</div>
                <div style={muted}>{[task && task.area, task && task.sub && task.sub.name].filter(Boolean).join(' · ')}</div>
              </div>
              <span style={bluePill}>{n} to walk</span>
              {task && <PrimaryButton onClick={() => setWalking(task)} style={smallBtn}>Walk {plural(n, 'line')}<Icon name="caret-r" size={13} /></PrimaryButton>}
            </div>
          );
        })}
      </RailBox>
    </>
  ) : nothing('No lines waiting for a spot check.');

  const verifiedSections = firstsOf(verified, (x) => dayKeyOf(verifiedAt(x)));
  const verifiedView = verified.length ? (
    <RailBox title="Days" resetKey={resetKey}>
      {verified.map((x, i) => {
        const k = dayKeyOf(verifiedAt(x));
        return row(x, i, {}, verifiedSections.first.has(x) ? railMark(`d-${k}`, dayLabelOf(verifiedAt(x)), verifiedSections.count.get(k)) : null);
      })}
    </RailBox>
  ) : nothing('Nothing verified yet.');

  const oldest = toVerify.length ? Math.max(...toVerify.map((x) => daysSince(doneAt(x)) || 0)) : 0;
  const sentBackCount = openAll.filter((x) => x.sentBack).length;
  const tiles = [
    ['verify', 'To verify', toVerify.length, toVerify.length ? (oldest ? `oldest waiting ${plural(oldest, 'day')}` : 'done today') : 'nothing waiting', 'var(--warning)'],
    ['open', 'Open with the subs', openAll.length, sentBackCount ? `${sentBackCount} sent back` : 'none sent back', 'var(--danger)'],
    ['walk', 'Lines to walk', toWalk.length, plural(walkTasks.length, 'task'), 'var(--accent-text)'],
    ['verified', 'Verified', verified.length, 'on this job', 'var(--text-1)'],
  ];
  const visibleTasks = subFilter ? tasks.filter((t) => String(t.sub.id) === subFilter) : tasks;

  return (
    <div ref={pageRef} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      {pageRail.on && (
        <div style={{ position: 'sticky', top: pageRail.head + 12, height: `calc(100vh - ${pageRail.head + 24}px)`, display: 'flex', flex: 'none' }}>
          <ScrollRail page box={null} items={pageRail.items} title="Sections" width={140} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
      <SubStyles />
      <style>{CSS}</style>
      {data.on === false && <div style={{ marginBottom: 12 }}><ErrorBanner>The punch list isn’t connected to E3 Subs yet.</ErrorBanner></div>}
      {data.error && <div style={{ marginBottom: 12 }}><ErrorBanner>{data.error} <button type="button" style={linkBtn} onClick={load}>Try again</button></ErrorBanner></div>}
      <nav aria-label="The punch list's sections" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 12 }}>
        {tiles.map(([key, text, n, sub, tone]) => {
          const hot = key === 'verify' && n > 0;
          return (
            <button key={key} type="button" className="pl-tile" title={`Go to ${text}`}
              onClick={() => {
                const el = document.getElementById(`punch-${key}`);
                if (el) window.scrollTo({ top: Math.max(0, el.getBoundingClientRect().top + window.scrollY - pageRail.head - 12), behavior: 'smooth' });
              }}
              style={{ padding: '8px 12px', borderRadius: 10, background: hot ? 'var(--warning-soft)' : 'var(--bg-surface-2)',
                border: `1px solid ${hot ? 'color-mix(in oklch, var(--warning) 45%, transparent)' : 'var(--border-subtle)'}` }}>
              <div style={{ ...muted, color: hot ? 'var(--warning)' : 'var(--text-3)', fontWeight: hot ? 650 : 400 }}>{text}</div>
              <div className="mono" style={{ fontSize: 21, fontWeight: 700, color: n ? tone : 'var(--text-1)' }}>{n}</div>
              <div style={{ fontSize: 11.5, color: hot ? 'var(--warning)' : 'var(--text-4)' }}>{sub}</div>
            </button>
          );
        })}
      </nav>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
        <input type="search" value={search} placeholder="Search what, where, line" aria-label="Search the punch list"
          onChange={(e) => setSearch(e.target.value)} style={{ ...select, width: 230 }} />
        <select value={subFilter} aria-label="Which sub" onChange={(e) => { setSubFilter(e.target.value); setTaskFilter(''); }} style={select}>
          <option value="">All subs</option>
          {subs.map((sb) => <option key={sb.id} value={String(sb.id)}>{sb.name}</option>)}
        </select>
        <select value={taskFilter} aria-label="Which task" onChange={(e) => setTaskFilter(e.target.value)} style={{ ...select, maxWidth: 320 }}>
          <option value="">All tasks</option>
          {visibleTasks.map((t) => <option key={t.id} value={String(t.id)}>{taskLabel(t)}</option>)}
        </select>
        <span style={{ flex: 1 }} />
        <PrimaryButton onClick={() => setAdding(taskFilter || '')} disabled={!tasks.length || data.on === false}
          title={tasks.length ? undefined : 'No sub has a task on this job yet: put one on a task on the Schedule first'}>
          <Icon name="plus" size={14} />Add punch item
        </PrimaryButton>
      </div>
      {!tasks.length && <div style={{ ...muted, margin: '6px 0' }}>No sub has a task on this job yet. Put a sub on a task on the Schedule first.</div>}
      {note && <div role="status" style={{ margin: '8px 0 0', padding: '8px 12px', borderRadius: 9, fontSize: 13.5, color: 'var(--success)', background: 'var(--success-soft)' }}>{note}</div>}
      {error && <div style={{ margin: '8px 0 0' }}><ErrorBanner>{error}</ErrorBanner></div>}

      <section aria-label="To verify">
        <div id="punch-verify" className="pl-sec" style={{ color: toVerify.length ? 'var(--warning)' : 'var(--text-1)' }}>
          <Icon name="check" size={16} stroke={2.2} />
          {toVerify.length ? `${plural(toVerify.length, 'item')} to verify — the sub says ${toVerify.length === 1 ? 'it’s' : 'they’re'} done` : 'To verify'}
          <span style={{ flex: 1 }} />
          {toVerify.length > 0 && (
            <PrimaryButton onClick={() => setReviewing(toVerify.map((x) => x.id))} style={{ ...amberBtn, ...smallBtn }}>Review one by one ({toVerify.length})</PrimaryButton>
          )}
        </div>
        {verifyView}
      </section>

      <section aria-label="Open with the subs">
        <div id="punch-open" className="pl-sec">
          Open with the subs {openAll.length > 0 && <span style={redPill}>{openAll.length}</span>}
          <span style={{ flex: 1 }} />
          {openAll.length > 0 && (
            <>
              <SecondaryButton aria-pressed={sentBackOnly} onClick={() => setSentBackOnly((v) => !v)}
                style={{ ...smallBtn, ...(sentBackOnly ? { color: 'var(--danger)', borderColor: 'var(--danger)' } : {}) }}>Sent back only</SecondaryButton>
              <select value={newestFirst ? 'newest' : 'oldest'} aria-label="Order" onChange={(e) => setNewestFirst(e.target.value === 'newest')} style={{ ...select, height: 30, fontSize: 13 }}>
                <option value="oldest">Oldest first</option>
                <option value="newest">Newest first</option>
              </select>
            </>
          )}
        </div>
        {openView}
      </section>

      <section aria-label="Lines to walk">
        <div id="punch-walk" className="pl-sec">
          Lines to walk {toWalk.length > 0 && <span style={bluePill}>{toWalk.length}</span>}
          {toWalk.length > 0 && <span style={{ ...muted, fontWeight: 400 }}>across {plural(walkTasks.length, 'task')} · walk them a task at a time</span>}
        </div>
        {walkView}
      </section>

      <section aria-label="Verified">
        <div id="punch-verified" className="pl-sec">
          Verified {verified.length > 0 && <span style={grayPill}>{verified.length}</span>}
        </div>
        {verifiedView}
      </section>

      {adding !== null && (
        <AddPunchDialog tasks={tasks} defaultSub={subFilter} defaultTask={adding} onClose={() => setAdding(null)}
          onAdded={(subName) => { setAdding(null); setNote(`Added. ${subName} hears about it in the next 7 AM email.`); load(); }} />
      )}
      {walking && <WalkTaskDialog task={walking} onClose={() => { setWalking(null); load(); }} />}
      {reviewing && (
        <ReviewDialog ids={reviewing} items={(data && data.items) || []} metaOf={metaOf} onExpired={onExpired}
          onClose={(said) => { setReviewing(null); if (said) setNote(said); load(); }} />
      )}
      {lift && liftedItem && (
        <Lift rect={lift.rect} from={lift.from} ariaLabel={`Punch item: ${liftedItem.what}`} line={liftLine(liftedItem, lift.opts)} closeRef={liftClose}
          paused={!!viewer || !!confirmDialogProps.open} onClosed={() => { setLift(null); setBack(null); }}>
          {liftPanel(liftedItem)}
        </Lift>
      )}
      {viewer && <PhotoViewer list={viewer.list} index={viewer.index} onIndex={(i) => setViewer({ ...viewer, index: i })} onClose={() => setViewer(null)} />}
      <ConfirmDialog {...confirmDialogProps} />
      </div>
    </div>
  );
}

/** A lifted item's photos: one big -- a click opens it full size -- and the others as thumbnails to pick from; the
    sub's latest (`theirs`, at the front) first. */
function LiftPhotos({ photos, theirs, onOpen, onExpired }) {
  const [shown, setShown] = useState(0);
  const at = Math.min(shown, photos.length - 1);
  const big = photos[at];
  return (
    <div style={{ minWidth: 0 }}>
      <button type="button" className="pl-lift-big" onClick={() => onOpen(at)} title="Open it full size"
        style={{ width: '100%', padding: 0, borderRadius: 10, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'zoom-in' }}>
        <img src={big.viewUrl || big.thumbUrl} alt="" onError={onExpired} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }} />
      </button>
      {photos.length > 1 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
          {photos.map((p, k) => (
            <span key={p.id} style={{ borderRadius: 11, boxShadow: k === at ? '0 0 0 2px var(--accent)' : 'none' }}>
              <Thumb photo={p} size={44} onOpen={() => setShown(k)} onExpired={onExpired} />
            </span>
          ))}
        </div>
      )}
      {theirs > 0 && <div style={{ ...muted, marginTop: 6 }}>{`The sub’s ${theirs === 1 ? 'photo' : `${theirs} photos`} first`}</div>}
    </div>
  );
}

/** A list in a short box that scrolls inside the tab, with the pencil-thin rail beside it (Duncan 2026-10-26: "a smaller
    list that can be scrolled on ... that pencil scroll indicator"), as the sub card's Requests and notes. Its sections'
    first rows carry railMark(); the rail is always there (Duncan 2026-10-26, on Open with 8 items that just fit: "Why
    does the open with subs not have the pencil scrolling thing?" -- always, his pick). A new view, search or filter
    (resetKey) starts it at the top. */
function RailBox({ children, title, resetKey }) {
  const boxRef = useRef(null);
  const [rail, setRail] = useState({ on: false, items: [], narrow: false });
  // What the rail was last set to. It's set again only when the measure changes: re-setting an unchanged rail from
  // this effect, which runs after every render, could loop until React gave up ("Something went wrong") when a click
  // folded a sub's group while the box was being measured (2026-10-26).
  const railKey = useRef(JSON.stringify(rail));
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    const measure = () => {
      const top0 = box.getBoundingClientRect().top - box.scrollTop;
      const items = [...box.querySelectorAll('[data-rail-id]')].map((el) => ({
        id: el.dataset.railId, label: el.dataset.railLabel, count: Number(el.dataset.railCount), top: Math.round(el.getBoundingClientRect().top - top0),
      }));
      const on = items.length > 0;
      const narrow = box.parentElement ? box.parentElement.clientWidth < 560 : false;
      const next = { on, items, narrow };
      const key = JSON.stringify(next);
      if (key === railKey.current) return;
      railKey.current = key;
      setRail(next);
    };
    measure();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure());
    if (ro) { ro.observe(box); if (box.firstElementChild) ro.observe(box.firstElementChild); }
    return () => { if (ro) ro.disconnect(); };
  });
  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = 0; }, [resetKey]);
  return (
    <div style={{ display: 'flex', border: '1px solid var(--border-default)', borderRadius: 10, background: 'var(--bg-surface)', overflow: 'hidden' }}>
      {rail.on && <ScrollRail box={boxRef} items={rail.items} narrow={rail.narrow} title={title} />}
      <div ref={boxRef} style={{ flex: 1, minWidth: 0, maxHeight: BOX_MAX, overflowY: 'auto' }}>
        <div>{children}</div>
      </div>
    </div>
  );
}

/** Review one by one (Duncan 2026-10-26, for a big job; the mockup he OK'd): the items the sub says are done, the oldest
    first, one at a time -- the sub's photos big, then E3's -- with Verify or Send back, each going straight to the next
    (his pick). V verifies, S starts Send back, the arrows move. */
function ReviewDialog({ ids, items, metaOf, onExpired, onClose }) {
  const byId = useMemo(() => new Map(items.map((x) => [x.id, x])), [items]);
  const [queue, setQueue] = useState(ids); // what's still to review
  const [at, setAt] = useState(0);
  const [tally, setTally] = useState({ verified: 0, sentBack: 0 });
  const [back, setBack] = useState(null); // the note while sending back
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [shown, setShown] = useState(0); // the photo showing big
  const index = Math.min(at, Math.max(0, queue.length - 1));
  const id = queue[index];
  const item = id ? byId.get(id) : null;
  useEffect(() => { setShown(0); setBack(null); setError(null); }, [id]);

  const acted = tally.verified + tally.sentBack;
  const finish = () => onClose(acted ? `Reviewed ${plural(acted, 'item')}: ${tally.verified} verified, ${tally.sentBack} sent back.` : null);
  const after = (key) => {
    setTally((t) => ({ ...t, [key]: t[key] + 1 }));
    setQueue((qq) => qq.filter((x) => x !== id));
    setAt((k) => Math.max(0, Math.min(k, queue.length - 2)));
  };
  const verify = async () => {
    if (!item || busy) return;
    setBusy('verify');
    setError(null);
    try {
      await verifyPunchItem(item.taskId, item.id, { requestId: newRequestId() });
      after('verified');
    } catch (e) {
      setError(e.message || 'That didn’t go through');
    } finally {
      setBusy(null);
    }
  };
  const sendBack = async () => {
    if (!item || busy || !back || !back.trim()) return;
    setBusy('back');
    setError(null);
    try {
      await sendBackPunchItem(item.taskId, item.id, { note: back.trim(), requestId: newRequestId() });
      after('sentBack');
    } catch (e) {
      setError(e.message || 'That didn’t go through');
    } finally {
      setBusy(null);
    }
  };
  const keys = useRef({});
  keys.current = { verify, startBack: () => setBack(''), count: queue.length };
  useEffect(() => {
    const onKey = (e) => {
      if (/^(INPUT|TEXTAREA|SELECT)$/.test((e.target && e.target.tagName) || '') || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'v' || e.key === 'V') { e.preventDefault(); keys.current.verify(); }
      else if (e.key === 's' || e.key === 'S') { e.preventDefault(); keys.current.startBack(); }
      else if (e.key === 'ArrowRight') setAt((k) => Math.min(k + 1, keys.current.count - 1));
      else if (e.key === 'ArrowLeft') setAt((k) => Math.max(k - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const total = ids.length;
  const added = item ? (item.thread || [])[0] : null;
  const lastDone = item ? lastOf(item, 'DONE') : null;
  const photos = item ? [...((lastDone && lastDone.photos) || []), ...((added && added.photos) || [])] : [];
  const big = photos[Math.min(shown, photos.length - 1)];
  const theirs = ((lastDone && lastDone.photos) || []).length;

  return (
    <ModalShell title={queue.length ? `Review · ${Math.min(acted + index + 1, total)} of ${total}` : 'Review'}
      subtitle={queue.length ? 'V verifies · S sends back · ← → move' : null}
      onClose={finish} busy={!!busy} width={980}
      footer={queue.length ? (
        <>
          <button type="button" style={{ ...linkBtn, color: index > 0 ? 'var(--accent-text)' : 'var(--text-4)' }} disabled={index === 0} onClick={() => setAt(index - 1)}>← Previous</button>
          <button type="button" style={{ ...linkBtn, color: index < queue.length - 1 ? 'var(--accent-text)' : 'var(--text-4)' }} disabled={index >= queue.length - 1} onClick={() => setAt(index + 1)}>Next →</button>
          <span style={{ flex: 1 }} />
          <SecondaryButton onClick={finish} disabled={!!busy}>Close</SecondaryButton>
        </>
      ) : <SecondaryButton onClick={finish}>Done</SecondaryButton>}>
      {!queue.length && (
        <div style={{ padding: '24px 4px', textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 650, color: 'var(--text-1)' }}>All {plural(total, 'item')} reviewed</div>
          <div style={{ ...muted, marginTop: 6 }}>{tally.verified} verified · {tally.sentBack} sent back</div>
        </div>
      )}
      {item && (
        <div className="pl-review">
          <div style={{ minWidth: 0 }}>
            <div className="pl-big" style={{ borderRadius: 10, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {big ? <img src={big.viewUrl} alt="" onError={onExpired} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }} />
                : <span style={muted}>No photos on this one</span>}
            </div>
            {photos.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
                {photos.map((p, k) => (
                  <span key={p.id} style={{ borderRadius: 11, boxShadow: k === shown ? '0 0 0 2px var(--accent)' : 'none' }}>
                    <Thumb photo={p} size={48} onOpen={() => setShown(k)} onExpired={onExpired} />
                  </span>
                ))}
                <span style={{ ...muted, marginLeft: 4 }}>{theirs ? `The sub’s ${theirs === 1 ? 'photo' : `${theirs} photos`} first` : 'E3’s photos'}</span>
              </div>
            )}
          </div>
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 650, color: 'var(--text-1)', lineHeight: 1.3 }}>{item.what}</div>
              <div style={{ ...muted, marginTop: 4 }}>{metaOf(item)}</div>
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              {(item.thread || []).map((e, i) => (
                <div key={i} style={{ fontSize: 13, color: 'var(--text-2)' }}>
                  <strong style={{ color: 'var(--text-1)', fontWeight: 600 }}>{STEP[e.kind] || e.kind}</strong>
                  {e.by ? ` by ${whoOf(e.by)}` : ''}{e.at ? `, ${whenOf(e.at)}` : ''}
                  {e.note && <div style={{ fontSize: 13.5, color: 'var(--text-1)', marginTop: 2, whiteSpace: 'pre-wrap' }}>{e.note}</div>}
                </div>
              ))}
            </div>
            {error && <ErrorBanner>{error}</ErrorBanner>}
            {back === null ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <SecondaryButton onClick={() => setBack('')} disabled={!!busy}>Send back…</SecondaryButton>
                <PrimaryButton busy={busy === 'verify'} onClick={verify}><Icon name="check" size={14} />Verify, next →</PrimaryButton>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                <textarea autoFocus value={back} rows={3} maxLength={2000} placeholder="What still needs doing" aria-label="What still needs doing"
                  onChange={(e) => setBack(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-1)', fontSize: 13.5, fontFamily: 'inherit', lineHeight: 1.4, resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <PrimaryButton busy={busy === 'back'} disabled={!back.trim()} onClick={sendBack}>Send back, next →</PrimaryButton>
                  <button type="button" style={{ ...linkBtn, color: 'var(--text-3)' }} onClick={() => setBack(null)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </ModalShell>
  );
}

/** Add punch item: the sub, one of its tasks (and on a task with lines, a line if it's about one), where, what, photos.
    The photos go to E3 Subs as they're picked (each its own call), then the item names them. */
function AddPunchDialog({ tasks, defaultSub, defaultTask, onClose, onAdded }) {
  const subs = useMemo(() => {
    const m = new Map();
    tasks.forEach((t) => { if (!m.has(t.sub.id)) m.set(t.sub.id, t.sub); });
    return [...m.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [tasks]);
  const startTask = defaultTask ? tasks.find((t) => String(t.id) === defaultTask) : null;
  const [subId, setSubId] = useState(startTask ? String(startTask.sub.id) : defaultSub || (subs.length === 1 ? String(subs[0].id) : ''));
  const [taskId, setTaskId] = useState(startTask ? String(startTask.id) : '');
  const [lineId, setLineId] = useState('');
  const [lines, setLines] = useState(null); // the task's lines; null while there are none (or they're loading)
  const [where, setWhere] = useState('');
  const [what, setWhat] = useState('');
  const [photos, setPhotos] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [requestId] = useState(newRequestId);
  const subTasks = tasks.filter((t) => String(t.sub.id) === subId);
  const task = tasks.find((t) => String(t.id) === taskId) || null;

  // A task's own photos and lines: picking another task starts both again.
  useEffect(() => {
    setLineId('');
    setPhotos([]);
    setLines(null);
    if (!task || !task.lines) return undefined;
    let live = true;
    getTaskWork(task.id).then((w) => { if (live) setLines((w && w.lines) || []); }).catch(() => { if (live) setLines([]); });
    return () => { live = false; };
  }, [taskId]);

  const sendPhotos = async (files) => {
    setBusy('photo');
    setError(null);
    try {
      for (const f of files) {
        const r = await addPunchPhoto(task.id, { ...(await photoBody(f)), requestId: newRequestId() }); // eslint-disable-line no-await-in-loop
        if (r && r.photo) setPhotos((p) => [...p, r.photo]);
      }
    } catch (e) {
      setError(e.message || 'The photo didn’t go');
    } finally {
      setBusy(null);
    }
  };
  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      await addPunchItem(task.id, {
        ...(lineId ? { lineId: Number(lineId) } : {}), ...(where.trim() ? { where: where.trim() } : {}), what: what.trim(),
        ...(photos.length ? { photoIds: photos.map((p) => p.id) } : {}), requestId,
      });
      onAdded(task.sub.name);
    } catch (e) {
      setError(e.message || 'It didn’t go through');
      setBusy(null);
    }
  };
  const ready = !!task && !!what.trim() && !busy;
  const lineText = (l) => `Line ${l.line_no ?? '—'}${[l.building, l.area].filter(Boolean).length ? ` · ${[l.building, l.area].filter(Boolean).join(' · ')}` : ''}${l.pro_type ? ` · ${l.pro_type}` : ''}`;

  return (
    <ModalShell title="Add punch item" subtitle="The sub hears about it in their next 7 AM email, and marks it done on E3 Subs."
      onClose={onClose} busy={busy === 'save'} width={600}
      footer={(
        <>
          <SecondaryButton onClick={onClose} disabled={busy === 'save'}>Cancel</SecondaryButton>
          <PrimaryButton busy={busy === 'save'} disabled={!ready} onClick={save}>Add punch item</PrimaryButton>
        </>
      )}>
      <div style={{ display: 'grid', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <Field id="punch-sub" label="Sub" required as="select" value={subId} onChange={(v) => { setSubId(v); setTaskId(''); }}
            options={[{ value: '', label: 'Pick the sub' }, ...subs.map((s) => ({ value: String(s.id), label: s.name }))]} />
          <Field id="punch-task" label="Task" required as="select" value={taskId} onChange={setTaskId} disabled={!subId}
            options={[{ value: '', label: subId ? 'Pick one of its tasks' : 'Pick the sub first' }, ...subTasks.map((t) => ({ value: String(t.id), label: taskLabel(t) }))]} />
        </div>
        {task && task.lines > 0 && (
          <Field id="punch-line" label="Line" hint="optional" as="select" value={lineId} onChange={setLineId} disabled={lines === null}
            options={[{ value: '', label: lines === null ? 'Loading its lines…' : 'Not one line: the task' }, ...(lines || []).map((l) => ({ value: String(l.id), label: lineText(l) }))]} />
        )}
        <Field id="punch-where" label="Where" hint="optional" value={where} onChange={setWhere} maxLength={200} placeholder="Room 104, east wall" />
        <Field id="punch-what" label="What needs fixing" required as="textarea" value={what} onChange={setWhat} maxLength={2000} placeholder="Lens cover cracked on the second fixture from the door" />
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>Photos <span style={{ fontWeight: 500, color: 'var(--text-4)' }}>optional</span></div>
          {task ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {photos.map((p, k) => <Thumb key={p.id} photo={p} size={52} onOpen={() => setViewer(k)} onRemove={() => setPhotos((x) => x.filter((y) => y.id !== p.id))} />)}
              <AddPhoto size={52} label="Add a photo" busy={busy === 'photo'} onFiles={sendPhotos} />
            </div>
          ) : <div style={muted}>Pick the task first.</div>}
        </div>
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
      {viewer != null && <PhotoViewer list={photos} index={viewer} onIndex={setViewer} onClose={() => setViewer(null)} />}
    </ModalShell>
  );
}

/** Walking one task's spot check (Duncan 2026-10-26: "I don't wanna click walk it for every single line item, it needs
    to be per task"; the mockup he OK'd): the task's picked lines in one window, in the workbook's order, each with its
    before and after photos and Passed, or Findings written up right on its row -- one line at a time (his pick: no
    "pass the rest"). A finding becomes a punch item for the sub, as from the Work window. */
function WalkTaskDialog({ task, onClose }) {
  const [rows, setRows] = useState(null); // [{ line: Summit's line, work: E3 Subs' state }], picked lines only
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState(null); // { lineId, type, note, photos }: Findings being written
  const [viewer, setViewer] = useState(null);
  const load = useCallback(() => Promise.all([getTaskWork(task.id), getTaskWorkProgress(task.id)]).then(([w, p]) => {
    if (!p || p.on === false) throw new Error('Line work isn’t connected to E3 Subs yet.');
    if (p.error) throw new Error(p.error);
    const byLine = new Map((p.lines || []).map((x) => [Number(x.lineId), x]));
    setRows(((w && w.lines) || []).map((l) => ({ line: l, work: byLine.get(l.id) })).filter((x) => x.work && x.work.spotCheck));
    setError(null);
  }).catch((e) => setError(e.message || 'The lines didn’t load')), [task.id]);
  useEffect(() => { load(); }, [load]);

  // E3 Subs answers a walk with the line's new state: that row changes, the rest stay.
  const apply = (lineId, line) => {
    if (line && line.spotCheck) setRows((rs) => rs.map((x) => (x.line.id === lineId ? { ...x, work: { ...x.work, ...line } } : x)));
    else load();
  };
  const pass = async (l) => {
    setBusy(`pass-${l.id}`);
    setError(null);
    try {
      const r = await recordSpotCheck(l.id, { result: 'PASSED', requestId: newRequestId() });
      apply(l.id, r && r.line);
    } catch (e) {
      setError(e.message || 'That didn’t go through');
    } finally {
      setBusy(null);
    }
  };
  const addPhotos = async (files) => {
    const lineId = form.lineId;
    setBusy('photo');
    setError(null);
    try {
      for (const f of files) {
        const r = await addWorkPhoto(lineId, { kind: 'finding', ...(await photoBody(f)), requestId: newRequestId() }); // eslint-disable-line no-await-in-loop
        if (r && r.photo) setForm((fm) => (fm && fm.lineId === lineId ? { ...fm, photos: [...fm.photos, r.photo] } : fm));
      }
    } catch (e) {
      setError(e.message || 'The photo didn’t go');
    } finally {
      setBusy(null);
    }
  };
  const saveFindings = async () => {
    setBusy('save');
    setError(null);
    try {
      const r = await recordSpotCheck(form.lineId, {
        result: 'FINDINGS', findings: [{ type: form.type, note: form.note.trim(), photoIds: form.photos.map((p) => p.id) }], requestId: newRequestId(),
      });
      apply(form.lineId, r && r.line);
      setForm(null);
    } catch (e) {
      setError(e.message || 'The findings weren’t saved');
    } finally {
      setBusy(null);
    }
  };

  const total = rows ? rows.length : 0;
  const walked = rows ? rows.filter((x) => x.work.spotCheck.status !== 'TO_WALK').length : 0;
  const place = (l) => [l.building, l.area].filter(Boolean).join(' · ');
  const ready = form && form.type && form.note.trim() && busy !== 'photo';

  return (
    <ModalShell title={`Walk ${task.code ? `${task.code} · ` : ''}${task.area || task.name}`}
      subtitle={rows ? `${walked} of ${plural(total, 'line')} walked${walked < total ? ' · Passed, or Findings to write one up' : ''}` : 'Loading the lines…'}
      onClose={onClose} busy={busy === 'save'} width={760}
      footer={<SecondaryButton onClick={onClose} disabled={busy === 'save'}>{rows && walked === total && total ? 'Done' : 'Close'}</SecondaryButton>}>
      <div style={{ height: 6, borderRadius: 3, background: 'var(--bg-surface-2)', marginBottom: 10, flexShrink: 0 }}>
        <div style={{ width: `${total ? Math.round((walked / total) * 100) : 0}%`, height: '100%', borderRadius: 3, background: 'var(--success)' }} />
      </div>
      {error && <div style={{ marginBottom: 10 }}><ErrorBanner>{error}</ErrorBanner></div>}
      {rows && !rows.length && <div style={muted}>No lines on this task are picked for a spot check.</div>}
      <div style={{ flex: '1 1 auto', border: rows && rows.length ? '1px solid var(--border-subtle)' : 'none', borderRadius: 10, overflow: 'auto', minHeight: 0 }}>
        {(rows || []).map(({ line: l, work: w }, i) => {
          const sc = w.spotCheck;
          const before = (w.photos && w.photos.before) || [];
          const after = (w.photos && w.photos.after) || [];
          const both = [...before, ...after];
          const writing = form && form.lineId === l.id;
          return (
            <div key={l.id} style={{ padding: '9px 12px', borderTop: i ? '1px solid var(--border-subtle)' : 'none', background: writing ? 'var(--bg-surface-2)' : 'transparent' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>Line {l.line_no ?? '—'}{l.pro_type ? ` · ${l.pro_type}` : ''}</span>
                  {place(l) && <span style={muted}> · {place(l)}</span>}
                </div>
                {before[0] && <Thumb photo={before[0]} size={34} onOpen={() => setViewer({ list: both, index: 0 })} onExpired={load} />}
                {after[0] && <Thumb photo={after[0]} size={34} onOpen={() => setViewer({ list: both, index: before.length })} onExpired={load} />}
                {sc.status === 'PASSED' && <span style={greenPill}><Icon name="check" size={12} />Passed</span>}
                {sc.status === 'FINDINGS' && <span style={redPill}>Findings · {[...new Set((sc.findings || []).map((f) => findingTypeOf(f.type)))].join(', ')}</span>}
                {sc.status === 'TO_WALK' && (
                  <>
                    <SecondaryButton busy={busy === `pass-${l.id}`} disabled={!!busy && busy !== `pass-${l.id}`} onClick={() => pass(l)} style={smallBtn}>
                      <Icon name="check" size={13} />Passed
                    </SecondaryButton>
                    <SecondaryButton danger disabled={!!busy} onClick={() => setForm(writing ? null : { lineId: l.id, type: '', note: '', photos: [] })} style={smallBtn}>
                      Findings
                    </SecondaryButton>
                  </>
                )}
              </div>
              {writing && (
                <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                  <div role="radiogroup" aria-label="What kind of finding" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {FINDING_TYPES.map(([v, text]) => (
                      <button key={v} type="button" role="radio" aria-checked={form.type === v} onClick={() => setForm({ ...form, type: v })}
                        style={{ height: 28, padding: '0 10px', borderRadius: 7, fontSize: 12.5, fontFamily: 'inherit', cursor: 'pointer',
                          border: `1px solid ${form.type === v ? 'var(--danger)' : 'var(--border-default)'}`,
                          background: form.type === v ? 'var(--danger-soft)' : 'var(--bg-surface)', color: form.type === v ? 'var(--danger)' : 'var(--text-2)', fontWeight: form.type === v ? 600 : 500 }}>
                        {text}
                      </button>
                    ))}
                  </div>
                  <textarea autoFocus value={form.note} rows={2} maxLength={2000} placeholder="What you found" aria-label="What you found"
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-1)', fontSize: 13.5, fontFamily: 'inherit', lineHeight: 1.4, resize: 'vertical' }} />
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {form.photos.map((p, k) => <Thumb key={p.id} photo={p} size={44} onOpen={() => setViewer({ list: form.photos, index: k })} onRemove={() => setForm({ ...form, photos: form.photos.filter((x) => x.id !== p.id) })} />)}
                    <AddPhoto compact label="Add a photo" busy={busy === 'photo'} onFiles={addPhotos} />
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <PrimaryButton busy={busy === 'save'} disabled={!ready} onClick={saveFindings} style={smallBtn}>Save findings</PrimaryButton>
                    <button type="button" style={{ ...linkBtn, color: 'var(--text-3)' }} onClick={() => setForm(null)}>Cancel</button>
                    {!(form.type && form.note.trim()) && <span style={muted}>Pick its kind and say what you found.</span>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {viewer && <PhotoViewer list={viewer.list} index={viewer.index} onIndex={(k) => setViewer({ ...viewer, index: k })} onClose={() => setViewer(null)} />}
    </ModalShell>
  );
}
